import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, History, Clock, User, FileText, CheckCircle, XCircle, AlertCircle, Send, Edit, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

const ACTION_ICONS = {
    created: FileText,
    sent_for_signature: Send,
    buyer_signed: CheckCircle,
    all_buyers_signed: CheckCircle,
    seller_accepted: CheckCircle,
    seller_rejected: XCircle,
    seller_counter_offer: Edit,
    status_changed: AlertCircle,
    expired: XCircle
};

const ACTION_COLORS = {
    created: 'text-blue-400 bg-blue-400/10',
    sent_for_signature: 'text-purple-400 bg-purple-500/10',
    buyer_signed: 'text-green-400 bg-green-400/10',
    all_buyers_signed: 'text-green-400 bg-green-400/10',
    seller_accepted: 'text-green-500 bg-green-500/10',
    seller_rejected: 'text-red-400 bg-red-400/10',
    seller_counter_offer: 'text-orange-400 bg-orange-400/10',
    status_changed: 'text-yellow-400 bg-yellow-400/10',
    expired: 'text-muted-foreground bg-gray-400/10'
};

export default function OfferHistoryModal({ isOpen, onClose, offerId, offerNumber }) {
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && offerId) {
            loadHistory();
        }
    }, [isOpen, offerId]);

    const loadHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/offers/${offerId}/history`);
            setHistory(response.data.history || []);
        } catch (err) {
            console.error('Error loading history:', err);
            setError('Impossible de charger l\'historique');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-border overflow-hidden max-h-[80vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-500/20 rounded-lg">
                                <History className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-white">Historique de l'offre</h2>
                                {offerNumber && (
                                    <p className="text-sm text-muted-foreground">{offerNumber}</p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-muted rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-muted-foreground" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                            </div>
                        ) : error ? (
                            <div className="text-center py-12">
                                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                                <p className="text-muted-foreground">{error}</p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={loadHistory}
                                    className="mt-4"
                                >
                                    Réessayer
                                </Button>
                            </div>
                        ) : history.length === 0 ? (
                            <div className="text-center py-12">
                                <History className="w-12 h-12 text-white/20 mx-auto mb-3" />
                                <p className="text-muted-foreground">Aucun historique disponible</p>
                            </div>
                        ) : (
                            <div className="relative">
                                {/* Timeline line */}
                                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-muted" />
                                
                                {/* Timeline items */}
                                <div className="space-y-6">
                                    {history.map((entry, index) => {
                                        const Icon = ACTION_ICONS[entry.action] || History;
                                        const colorClass = ACTION_COLORS[entry.action] || 'text-muted-foreground bg-gray-400/10';
                                        
                                        return (
                                            <motion.div
                                                key={entry.id || index}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="relative pl-12"
                                            >
                                                {/* Icon */}
                                                <div className={`absolute left-0 p-2 rounded-full ${colorClass}`}>
                                                    <Icon className="w-4 h-4" />
                                                </div>
                                                
                                                {/* Content */}
                                                <div className="bg-card rounded-lg p-4">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <h4 className="font-medium text-white">
                                                            {entry.action_label || entry.action}
                                                        </h4>
                                                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                            {formatDate(entry.timestamp)}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* User info */}
                                                    {entry.user_name && entry.user_name !== "Système" && (
                                                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                                                            <User className="w-3 h-3" />
                                                            <span>{entry.user_name}</span>
                                                            {entry.user_role && (
                                                                <span className="px-1.5 py-0.5 text-xs bg-muted rounded">
                                                                    {entry.user_role === 'buyer' ? 'Acquéreur' : 
                                                                     entry.user_role === 'seller' ? 'Vendeur' :
                                                                     entry.user_role === 'agent' ? 'Agent' : entry.user_role}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    {/* Details */}
                                                    {entry.details && Object.keys(entry.details).length > 0 && (
                                                        <div className="mt-2 text-sm text-muted-foreground">
                                                            {entry.details.offer_amount && (
                                                                <p>Montant : {entry.details.offer_amount.toLocaleString('fr-FR')} €</p>
                                                            )}
                                                            {entry.details.counter_amount && (
                                                                <p>Contre-offre : {entry.details.counter_amount.toLocaleString('fr-FR')} €</p>
                                                            )}
                                                            {entry.details.seller_message && (
                                                                <p className="italic">"{entry.details.seller_message}"</p>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    {/* Status change */}
                                                    {entry.old_value && entry.new_value && (
                                                        <div className="mt-2 flex items-center gap-2 text-xs">
                                                            <span className="px-2 py-1 bg-muted rounded text-muted-foreground">
                                                                {entry.old_value}
                                                            </span>
                                                            <span className="text-white/30">→</span>
                                                            <span className="px-2 py-1 bg-purple-500/20 rounded text-purple-300">
                                                                {entry.new_value}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-3 border-t border-border flex justify-end flex-shrink-0">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="border-border text-muted-foreground"
                        >
                            Fermer
                        </Button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
